/**
 * 🧑‍💼 **사람이 할 일이 끝났는지 — 운영에서 물어 보는 판정** (2026-09-26).
 *
 * 개발자는 코드를 올리지만, 아이 화면에 닿는 마지막 한 걸음은 운영자가 관리자에서 누른다
 * (영유아 모드 게시 · 마법 올리기 · 주소 채우기 · 보호자 문구). 그 걸음이 안 밟혀도 **아무 에러가 없다** —
 * 운영 설정이 `auto` 로 남아 첫 화면 요청이 목표를 넘었고, 첨성대 카드는 12일째 비춰도 아무 일이 없었다.
 *
 * `pnpm check:owner` 가 이것들로 운영을 물어 ✅/⬜ 로 보여 준다. 🔴 판정은 **여기 한 곳** —
 * 화면(웹)과 점검(스크립트)이 따로 짜면 「점검은 ✅ 인데 화면은 안 바뀐다」가 된다.
 */
// 🔴 확장자 — scripts/check-owner.mjs 가 node 로 직접 읽는다
import { configuredUrl } from "./placeholder.ts";
import { ageBandsOf } from "./ageBands.ts";

/** 영유아 모드가 운영에 게시됐나. 저장된 값(`auto`)이 코드 기본값을 이기므로 게시해야만 바뀐다. */
export function kidsModePublished(settings: { mode?: unknown } | null | undefined): boolean {
  return settings?.mode === "kids";
}

/**
 * 🎴 카드의 「영상으로 보기」 주소 — **아이 화면이 쓰는 그 주소**.
 * 게시 매니페스트의 `ar.target.<카드>.fallbackYoutube` 가 먼저, 없으면 ar-scenes.json 의 `fallback_youtube`.
 * 자리표시자(`REPLACE`)는 없는 것으로 친다(`configuredUrl`).
 */
export function cardVideoUrl(
  target: { fallbackYoutube?: unknown } | undefined,
  legacy: unknown,
): string | undefined {
  const fromTarget = typeof target?.fallbackYoutube === "string" ? target.fallbackYoutube : undefined;
  return configuredUrl(fromTarget) ?? configuredUrl(typeof legacy === "string" ? legacy : undefined);
}

/**
 * 🧑‍🍼 보호자용 함께 읽기 문구가 있는 마법 장면 수. 마법 정의(`interact`)가 있는 `ar.target.*` 만 센다.
 * 문구는 층·자리 어디에 있어도 된다(`guardian_ko` 가 비지 않은 것이 하나라도 있으면).
 * 아이 화면이 보는 매니페스트(정적 + 게시본 합친 것)를 넘길 것.
 */
export function guardianLines(
  assets: Record<string, { interact?: unknown; hidden?: unknown; offWeb?: unknown } | undefined>,
): {
  done: number;
  missing: string[];
} {
  let done = 0;
  const missing: string[] = [];
  for (const [key, entry] of Object.entries(assets)) {
    if (!key.startsWith("ar.target.") || !entry?.interact) continue;
    // 웹에서 내린 장면은 아이에게 안 나가니 세지 않는다(관리자는 내린 것도 목록에 남겨 둔다)
    if (entry.hidden || entry.offWeb) continue;
    if (/"guardian_ko"\s*:\s*"[^"]/.test(JSON.stringify(entry.interact))) done++;
    else missing.push(key.slice("ar.target.".length));
  }
  return { done, missing };
}

/**
 * 🚪 **이벤트 광장 옆문**(마을 (5,8) → event) — 관찰 테스트 ③ 「마을 걷기」에서 무대로 곧장 끌면 들어가야 한다.
 * 계단 문(3,10)만 있으면 오른쪽 기둥에 막혀 위로 미끄러진다(2026-09-27 걸어 봄).
 */
export function hasEventSideDoor(map: unknown): boolean {
  const rawLayers = (map as { layers?: unknown } | null)?.layers;
  const layers = (Array.isArray(rawLayers) ? rawLayers : []) as ({ objects?: unknown } | null)[]; // 🔴 배열 아님이면 던졌다(2026-10-11)
  return layers
    .flatMap((l) => (Array.isArray(l?.objects) ? l.objects : []))
    .some((o) => {
      const ob = o as { type?: unknown; x?: unknown; y?: unknown; properties?: { name?: unknown; value?: unknown }[] };
      const to = (Array.isArray(ob.properties) ? ob.properties : []).find((p) => p?.name === "to")?.value;
      return ob.type === "portal" && to === "event" && Math.round(Number(ob.x)) === 240 && Math.round(Number(ob.y)) === 384;
    });
}

/**
 * 🗓 **사람 일의 약속 날짜** — 조치계획서(docs/kpipa/4)에 적어 둔 기한. 키는 출시 준비 항목 id(`releaseChecks`).
 * 화면과 `pnpm check:owner` 가 같은 날짜를 말하게 여기 한 곳에 둔다.
 * (계약·서류 같은 사람 일은 넣지 않는다 — 관리자 화면은 바깥에 배포되는 묶음이다)
 */
export const OWNER_DUE: Readonly<Record<string, string>> = {
  "ar-video": "2026-09-30",
  "rally-form": "2026-09-30",
  "landing-age": "2026-10-10",
  "side-door": "2026-10-13",
  guardian: "2026-10-31",
};

/** 오늘에서 기한까지 남은 날(지났으면 음수). 날짜는 **이 기기의 달력 날짜**로 센다(UTC 로 세면 한국 새벽에 하루 어긋난다). */
export function daysUntil(due: string, today: Date): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(due);
  if (!m) return Number.NaN;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

/** 기한 딱지 — 「9일 지남」·「오늘까지」·「D-4」·「10. 31.까지」. */
export function dueSay(due: string, today: Date): string {
  const n = daysUntil(due, today);
  if (Number.isNaN(n)) return "";
  if (n < 0) return `${-n}일 지남`;
  if (n === 0) return "오늘까지";
  if (n <= 14) return `D-${n}`;
  const [, mo, da] = due.split("-");
  return `${Number(mo)}. ${Number(da)}.까지`;
}

/**
 * 👶 **첫 화면·책 소개 연령 딱지 + 홍보 문구**(조치계획서 ■9, 10. 10.) — 운영자가 관리자 문구 화면에서 넣는다.
 * 연령은 세 단(0~3세·2세~·4~7세)이 약속이라 딱지 3개 이상이면 끝. 홍보 문구 「그림책이 살아나는 놀이터」는
 * **첫 화면에 보이는 칸**(제목·말풍선·큰 단추·배지·연령 딱지) 어딘가에 「살아나는」이 있으면 끝으로 친다 —
 * 어느 칸에 넣을지는 운영자 몫이다. (보호자 안내 글의 「놀이터」는 첫 화면이 아니라 세지 않는다)
 */
export function landingAgeAndTagline(text: object | null | undefined): {
  bands: number;
  tagline: boolean;
} {
  // 관리자의 SiteText 도, 엣지에서 받은 날것도 받는다 — 칸 이름으로만 읽는다
  const t = text as Record<string, unknown> | null | undefined;
  const s = (k: string) => (typeof t?.[k] === "string" ? (t[k] as string) : "");
  const shown = ["milestone", "title1", "title2", "bubble", "cta", "ageBands"].map(s).join(" ");
  return { bands: ageBandsOf(s("ageBands")).length, tagline: shown.includes("살아나는") };
}
